import { useMemo, useState } from 'react'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { ChevronUpDownIcon } from '@heroicons/react/20/solid'

import { cn } from '@/lib/utils'

export interface ScrollableOption {
  id: string | number
  label: string
  disabled?: boolean
}

interface ScrollableOptionListProps {
  /**
   * Options to display
   */
  options: ScrollableOption[]
  /**
   * Current selected value(s)
   */
  value?: string | number | string[] | number[] | null
  /**
   * Callback when selection changes
   */
  onChange: (_value: string | number | null | (string | number)[]) => void
  /**
   * Message when no options
   */
  emptyMessage?: string
  /**
   * Disabled state
   */
  disabled?: boolean
  /**
   * Allow multiple selection
   */
  multiple?: boolean
  /**
   * Fixed height for container (in pixels) - Default 200px based on Figma
   */
  containerHeight?: number
  /**
   * Custom className
   */
  className?: string
  /**
   * Loading state
   */
  isLoading?: boolean
  /**
   * Render collapsed as a popover trigger (default false)
   */
  collapsed?: boolean
  /**
   * Placeholder text to show on trigger when collapsed
   */
  placeholder?: string
}

/**
 * ScrollableOptionList - Simple scrollable list without search
 * Designed for smaller lists or when search is not needed
 * Based on Figma design specifications
 *
 * Usage:
 * ```tsx
 * <ScrollableOptionList
 *   options={categoryOptions}
 *   value={selectedCategory}
 *   onChange={setSelectedCategory}
 *   containerHeight={180}
 * />
 * ```
 */
export function ScrollableOptionList({
  options,
  value,
  onChange,
  emptyMessage = 'No hay opciones disponibles',
  disabled = false,
  multiple = false,
  containerHeight = 200,
  className,
  isLoading = false,
  collapsed = false,
  placeholder = 'Seleccionar opción',
}: ScrollableOptionListProps) {
  const [isOpen, setIsOpen] = useState(false)

  // Get selected option(s) for display
  const selectedOptions = useMemo(() => {
    if (multiple && Array.isArray(value)) {
      return options.filter((opt) =>
        (value as (string | number)[]).includes(opt.id)
      )
    }
    if (!multiple && value !== null && value !== undefined) {
      return options.find((opt) => opt.id === value)
    }
    return multiple ? [] : null
  }, [value, options, multiple])

  // Check if an option is selected
  const isSelected = (option: ScrollableOption) => {
    if (multiple && Array.isArray(value)) {
      return (value as (string | number)[]).includes(option.id)
    }
    return value === option.id
  }

  // Handle option selection
  const handleSelect = (option: ScrollableOption) => {
    if (disabled || option.disabled) return

    if (multiple) {
      const currentValues = (Array.isArray(value) ? value : []) as (
        | string
        | number
      )[]
      if (currentValues.includes(option.id)) {
        const newValues = currentValues.filter((id) => id !== option.id)
        onChange(newValues)
      } else {
        const newValues = [...currentValues, option.id]
        onChange(newValues)
      }
    } else {
      onChange(option.id)
    }
  }

  return (
    <div className={cn('relative', className)}>
      {collapsed ? (
        <PopoverPrimitive.Root onOpenChange={setIsOpen} open={isOpen}>
          <PopoverPrimitive.Trigger asChild>
            <button
              className={cn(
                'relative flex h-9 w-full items-center rounded-md border bg-neutral-50 pl-3 pr-10',
                'text-left shadow-sm focus:outline-none focus:ring-1 sm:text-sm',
                disabled
                  ? 'cursor-not-allowed border-neutral-300 opacity-60'
                  : 'border-neutral-300 focus:border-neutral-500 focus:ring-neutral-500'
              )}
              disabled={disabled}
              type="button"
            >
              {multiple ? (
                <div className="flex flex-wrap gap-1">
                  {Array.isArray(selectedOptions) &&
                  selectedOptions.length > 0 ? (
                    selectedOptions.map((opt: ScrollableOption) => (
                      <span
                        className="rounded bg-neutral-200 px-2 py-0.5 text-xs"
                        key={opt.id}
                      >
                        {opt.label}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-500">{placeholder}</span>
                  )}
                </div>
              ) : (
                <span
                  className={cn(
                    'block truncate',
                    selectedOptions ? 'text-gray-900' : 'text-gray-500'
                  )}
                >
                  {selectedOptions
                    ? (selectedOptions as ScrollableOption).label
                    : placeholder}
                </span>
              )}

              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                {isLoading ? (
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
                ) : (
                  <ChevronUpDownIcon
                    aria-hidden="true"
                    className="h-4 w-4 text-gray-400 opacity-100"
                  />
                )}
              </span>
            </button>
          </PopoverPrimitive.Trigger>

          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
              align="start"
              className={cn(
                'z-[9999] w-[--radix-popover-trigger-width]',
                'overflow-hidden rounded-md border border-neutral-300 bg-neutral-50',
                'shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-2px_rgba(0,0,0,0.1)]',
                'data-[state=open]:animate-in data-[state=closed]:animate-out',
                'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95'
              )}
              onWheel={(e) => {
                e.stopPropagation()
              }}
              side="bottom"
              sideOffset={4}
            >
              <div
                className={cn(
                  'scrollbar-thumb-rounded-full flex flex-col items-start gap-0',
                  'overflow-y-auto p-2',
                  'scrollbar-thin scrollbar-track-transparent scrollbar-thumb-[#737373]',
                  'touch-pan-y'
                )}
                onTouchMove={(event) => event.stopPropagation()}
                onTouchStart={(event) => event.stopPropagation()}
                style={{
                  maxHeight: `${containerHeight}px`,
                  WebkitOverflowScrolling: 'touch',
                  overscrollBehavior: 'contain',
                }}
              >
                {isLoading ? (
                  <div className="flex w-full items-center justify-center py-8">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
                  </div>
                ) : options.length === 0 ? (
                  <div className="w-full py-4 text-center text-sm text-gray-500">
                    {emptyMessage}
                  </div>
                ) : (
                  options.map((option) => (
                    <button
                      className={cn(
                        'flex h-8 min-h-[32px] w-full flex-row items-center gap-2',
                        'self-stretch rounded-md px-2 py-1 transition-colors',
                        option.disabled
                          ? 'cursor-not-allowed bg-neutral-50 text-gray-400'
                          : 'cursor-pointer bg-neutral-50 text-[#262626] hover:bg-neutral-100',
                        isSelected(option) &&
                          !option.disabled &&
                          'bg-neutral-100'
                      )}
                      disabled={option.disabled || disabled}
                      key={option.id}
                      onClick={() => handleSelect(option)}
                      type="button"
                    >
                      <div className="flex h-5 flex-1 flex-col items-start justify-center gap-0">
                        <span
                          className={cn(
                            'w-full text-left text-sm font-normal leading-5',
                            'tracking-[0.005em] text-[#262626]',
                            isSelected(option) && 'font-semibold'
                          )}
                        >
                          {option.label}
                        </span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
      ) : (
        <div
          className={cn(
            'flex flex-col items-start gap-0 rounded-md border border-[#D4D4D4]',
            'bg-[#FAFAFA]'
          )}
          style={{ height: `${containerHeight}px` }}
        >
          {/* Content / Options List */}
          <div className="flex flex-1 flex-row items-start self-stretch overflow-hidden rounded-md">
            <div
              className={cn(
                'scrollbar-thumb-rounded-full flex flex-1 flex-col items-start gap-0',
                'self-stretch overflow-y-auto p-2',
                'scrollbar-thin scrollbar-track-transparent scrollbar-thumb-[#737373]',
                'touch-pan-y'
              )}
              onTouchMove={(event) => event.stopPropagation()}
              onTouchStart={(event) => event.stopPropagation()}
              style={{
                WebkitOverflowScrolling: 'touch',
                overscrollBehavior: 'contain',
              }}
            >
              {isLoading ? (
                <div className="flex w-full items-center justify-center py-8">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
                </div>
              ) : options.length === 0 ? (
                <div className="w-full py-4 text-center text-sm text-gray-500">
                  {emptyMessage}
                </div>
              ) : (
                options.map((option) => (
                  <button
                    className={cn(
                      'flex h-8 min-h-[32px] w-full flex-row items-center gap-2',
                      'self-stretch rounded-md px-2 py-1 transition-colors',
                      option.disabled
                        ? 'cursor-not-allowed bg-[#FAFAFA] text-gray-400'
                        : 'cursor-pointer bg-[#FAFAFA] text-[#262626] hover:bg-neutral-100',
                      isSelected(option) && !option.disabled && 'bg-neutral-100'
                    )}
                    disabled={option.disabled || disabled}
                    key={option.id}
                    onClick={() => handleSelect(option)}
                    type="button"
                  >
                    <div className="flex h-5 flex-1 flex-col items-start justify-center gap-0">
                      <span
                        className={cn(
                          'w-full text-left text-sm font-normal leading-5',
                          'tracking-[0.005em] text-[#262626]',
                          isSelected(option) && 'font-semibold'
                        )}
                      >
                        {option.label}
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
